/**
 * Forward paper test of "Breakout Watch" (BW1), from research/breakout
 * (RESULTS.md, PR #168): when a top coin closes an hour above the highest
 * price of the 72 hours before it, after those 72 hours stayed within a 15%
 * range, on at least twice its normal daily volume, buy at the next hour's
 * open, then sell at +50%, at −20%, or after 30 days, whichever comes first.
 *
 * This is the backtest's pre-registered pick, unchanged, and it FAILED its
 * backtest bar (about break-even after fees). It is tracked anyway because
 * its signals preceded +30% weeks 2.5–4× more often than equally volatile
 * hours, so the page records two things for every signal from registration
 * on: the paper trade, and whether the coin touched +30% (or −30%) within the
 * next 7 days. Nothing here trades. Pure logic: no network, no storage.
 *
 * Timing matches research/breakout/signals.ts and research/dip-buyer/engine.ts:
 * - Hourly candles are keyed by bucket START; a signal on bar i fills at bar
 *   i+1's open.
 * - Volume ratio at bar i = volume of bars i-23..i ÷ the median of the 30
 *   prior 24-bar sums (ending at bars i-24, i-48, …). Windows count bars,
 *   not clock hours, like the backtest.
 * - Exits are checked from the entry bar on; stop first if one bar touches
 *   both; a later bar that opens past a level fills at its open; the time
 *   stop sells at the close of the bar where hours held (entry bar = 1)
 *   reaches 720.
 * - After an exit, the coin waits 72 hours before a new signal can count.
 * - A 7-day outcome starts at every signal ONSET (signal on bar i, not on bar
 *   i-1), traded or not: the highest high and lowest low over bars
 *   i+1..i+168, against bar i+1's open.
 */
import type { Candle } from '@whaletail/signal-engine';
import {
  CRASH_BOUNCE_COSTS,
  clusterMeanInterval,
  netReturn,
  weekOf,
} from './crashBounce';

export const BREAKOUT_RULE = {
  id: 'BW1',
  label: 'Breakout Watch: a close above a tight 3-day range on 2× volume',
  rangeH: 72,
  rangePct: 0.15,
  volMult: 2,
  volDay: 24,
  volBaseDays: 30,
  targetPct: 0.5,
  stopPct: 0.2,
  maxHoldH: 720,
  cooldownH: 72,
  /** 7-day outcome: did the high reach +30%, or the low −30%? */
  outcomeH: 168,
  moonPct: 0.3,
  craterPct: 0.3,
  /** Only signals on bars starting at or after this instant count. */
  registeredAtMs: Date.parse('2026-10-15T00:00:00Z'),
  /** Same Coinbase USD markets as Crash Bounce. Fixed at registration. */
  coins: [
    'BTC',
    'ETH',
    'XRP',
    'SOL',
    'BNB',
    'DOGE',
    'ADA',
    'LINK',
    'XLM',
    'LTC',
    'BCH',
    'HYPE',
    'ZEC',
    'NEAR',
    'UNI',
    'DOT',
    'AVAX',
    'SHIB',
    'ATOM',
    'ETC',
    'TON',
  ],
} as const;

/** The rule's numbers, loosened from literals so tests can vary them. */
export interface BreakoutRuleParams {
  rangeH: number;
  rangePct: number;
  volMult: number;
  volDay: number;
  volBaseDays: number;
  targetPct: number;
  stopPct: number;
  maxHoldH: number;
  cooldownH: number;
  outcomeH: number;
  moonPct: number;
  craterPct: number;
  registeredAtMs: number;
}

/** Same cost model as Crash Bounce; the pass bar is judged at the high fee. */
export const BREAKOUT_COSTS = CRASH_BOUNCE_COSTS;

/** Set before any forward data existed (same shape as Crash Bounce's). */
export const BREAKOUT_PASS_BAR = {
  minTrades: 30,
  minWeeks: 8,
  feePerSide: BREAKOUT_COSTS.feeHigh,
  minLowerBound: 0,
  /** No stop may fill more than this far past the −20% stop. */
  maxStopOvershoot: 0.1,
} as const;

/**
 * What the backtest saw, for comparison on the page (research/breakout,
 * pit2023 universe, TEST 2025-01 → 2026-10): share of signal onsets followed
 * by a +30% week, and the share of all hours in the same volatility decile.
 */
export const BREAKOUT_BACKTEST = {
  moonRateAfterSignal: 0.105,
  moonRateVolMatched: 0.028,
} as const;

export const BREAKOUT_STAKE_DOLLARS = 1_000;

/** Bars needed behind a bar for its volume ratio. */
export const BREAKOUT_WARMUP_BARS =
  BREAKOUT_RULE.volDay * (BREAKOUT_RULE.volBaseDays + 1);

const HOUR_MS = 3_600_000;

export type BreakoutExitReason = 'target' | 'stop' | 'time';

export interface BreakoutTrade {
  coin: string;
  /** Start of the bar whose close triggered the signal. */
  signalBarMs: number;
  signalClose: number;
  /** The prior 72-hour high the close broke above. */
  priorHigh: number;
  volumeRatio: number;
  /** Start of the entry bar; the fill is its open. */
  entryMs: number;
  /** Fill price incl. slippage. */
  entryPx: number;
  exitMs?: number;
  exitPx?: number;
  reason?: BreakoutExitReason;
}

/** A signal's 7-day aftermath, open until `bars` reaches the rule's outcomeH. */
export interface BreakoutOutcome {
  coin: string;
  signalBarMs: number;
  /** Bar i+1's open (no slippage), null until that bar is seen. */
  refPx: number | null;
  maxHigh: number;
  minLow: number;
  /** Bars seen after the signal bar. */
  bars: number;
  /** Whether this onset became a paper trade. */
  traded: boolean;
}

export interface PendingEntry {
  signalBarMs: number;
  signalClose: number;
  priorHigh: number;
  volumeRatio: number;
}

export interface BreakoutCoinState {
  lastBarMs: number | null;
  lastClose: number | null;
  /** Readings at the last bar, for the watchlist. */
  volumeRatio: number | null;
  priorHigh: number | null;
  /** (high − low) ÷ close over the 72 bars before the last bar. */
  priorRange: number | null;
  /** Whether the signal fired on the last bar (to detect onsets). */
  lastFired: boolean;
  pending: PendingEntry | null;
  open: BreakoutTrade | null;
  cooldownUntilMs: number;
  /** Outcomes still inside their 7-day window. */
  watching: BreakoutOutcome[];
}

export interface BreakoutWatchState {
  schemaVersion: 1;
  coins: Record<string, BreakoutCoinState>;
  /** Closed trades, oldest first. */
  trades: BreakoutTrade[];
  /** Finished 7-day outcomes, oldest first. */
  outcomes: BreakoutOutcome[];
  lastRunMs: number | null;
  lastFailures: string[];
}

export const MAX_BREAKOUT_RECORDS = 5_000;

export function emptyBreakoutCoinState(): BreakoutCoinState {
  return {
    lastBarMs: null,
    lastClose: null,
    volumeRatio: null,
    priorHigh: null,
    priorRange: null,
    lastFired: false,
    pending: null,
    open: null,
    cooldownUntilMs: 0,
    watching: [],
  };
}

export function emptyBreakoutWatchState(): BreakoutWatchState {
  return {
    schemaVersion: 1,
    coins: {},
    trades: [],
    outcomes: [],
    lastRunMs: null,
    lastFailures: [],
  };
}

// ---- parsing (state comes back from a blob; never trust its shape) ----

const isNum = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

function parseTrade(raw: unknown): BreakoutTrade | null {
  const t = raw as Partial<BreakoutTrade> | null;
  if (
    !t ||
    typeof t.coin !== 'string' ||
    !isNum(t.signalBarMs) ||
    !isNum(t.signalClose) ||
    !isNum(t.priorHigh) ||
    !isNum(t.volumeRatio) ||
    !isNum(t.entryMs) ||
    !isNum(t.entryPx)
  ) {
    return null;
  }
  const trade: BreakoutTrade = {
    coin: t.coin,
    signalBarMs: t.signalBarMs,
    signalClose: t.signalClose,
    priorHigh: t.priorHigh,
    volumeRatio: t.volumeRatio,
    entryMs: t.entryMs,
    entryPx: t.entryPx,
  };
  if (
    isNum(t.exitMs) &&
    isNum(t.exitPx) &&
    (t.reason === 'target' || t.reason === 'stop' || t.reason === 'time')
  ) {
    trade.exitMs = t.exitMs;
    trade.exitPx = t.exitPx;
    trade.reason = t.reason;
  }
  return trade;
}

function parseOutcome(raw: unknown): BreakoutOutcome | null {
  const o = raw as Partial<BreakoutOutcome> | null;
  if (
    !o ||
    typeof o.coin !== 'string' ||
    !isNum(o.signalBarMs) ||
    !(o.refPx === null || isNum(o.refPx)) ||
    !isNum(o.maxHigh) ||
    !isNum(o.minLow) ||
    !isNum(o.bars) ||
    typeof o.traded !== 'boolean'
  ) {
    return null;
  }
  return {
    coin: o.coin,
    signalBarMs: o.signalBarMs,
    refPx: o.refPx,
    maxHigh: o.maxHigh,
    minLow: o.minLow,
    bars: o.bars,
    traded: o.traded,
  };
}

function parseCoin(raw: unknown): BreakoutCoinState {
  const c = raw as Partial<BreakoutCoinState> | null;
  const s = emptyBreakoutCoinState();
  if (!c) return s;
  if (isNum(c.lastBarMs)) s.lastBarMs = c.lastBarMs;
  if (isNum(c.lastClose)) s.lastClose = c.lastClose;
  if (isNum(c.volumeRatio)) s.volumeRatio = c.volumeRatio;
  if (isNum(c.priorHigh)) s.priorHigh = c.priorHigh;
  if (isNum(c.priorRange)) s.priorRange = c.priorRange;
  if (typeof c.lastFired === 'boolean') s.lastFired = c.lastFired;
  if (isNum(c.cooldownUntilMs)) s.cooldownUntilMs = c.cooldownUntilMs;
  const p = c.pending;
  if (
    p &&
    isNum(p.signalBarMs) &&
    isNum(p.signalClose) &&
    isNum(p.priorHigh) &&
    isNum(p.volumeRatio)
  ) {
    s.pending = { ...p };
  }
  const open = parseTrade(c.open);
  if (open && open.reason === undefined) s.open = open;
  if (Array.isArray(c.watching)) {
    s.watching = c.watching
      .map(parseOutcome)
      .filter((o): o is BreakoutOutcome => o !== null);
  }
  return s;
}

export function parseBreakoutWatchState(raw: string): BreakoutWatchState {
  try {
    const parsed = JSON.parse(raw) as Partial<BreakoutWatchState>;
    if (parsed?.schemaVersion !== 1) return emptyBreakoutWatchState();
    const coins: Record<string, BreakoutCoinState> = {};
    for (const [k, v] of Object.entries(parsed.coins ?? {})) coins[k] = parseCoin(v);
    const trades = (Array.isArray(parsed.trades) ? parsed.trades : [])
      .map(parseTrade)
      .filter((t): t is BreakoutTrade => t !== null && t.reason !== undefined);
    const outcomes = (Array.isArray(parsed.outcomes) ? parsed.outcomes : [])
      .map(parseOutcome)
      .filter((o): o is BreakoutOutcome => o !== null);
    return {
      schemaVersion: 1,
      coins,
      trades,
      outcomes,
      lastRunMs: isNum(parsed.lastRunMs) ? parsed.lastRunMs : null,
      lastFailures: Array.isArray(parsed.lastFailures)
        ? parsed.lastFailures.filter((x): x is string => typeof x === 'string')
        : [],
    };
  } catch {
    return emptyBreakoutWatchState();
  }
}

// ---- signal ----

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export interface BreakoutReading {
  volumeRatio: number | null;
  priorHigh: number | null;
  priorRange: number | null;
  fired: boolean;
}

/**
 * The signal on bar i of `candles` (sorted, oldest first). Readings are null
 * when the array doesn't reach far enough back; a null reading never fires.
 */
export function breakoutReading(
  candles: readonly Candle[],
  i: number,
  rule: BreakoutRuleParams = BREAKOUT_RULE,
): BreakoutReading {
  let volumeRatio: number | null = null;
  const warmup = rule.volDay * (rule.volBaseDays + 1) - 1;
  if (i >= warmup) {
    const daySum = (end: number) => {
      let s = 0;
      for (let k = end - rule.volDay + 1; k <= end; k++) s += candles[k].volume;
      return s;
    };
    const base: number[] = [];
    for (let d = 1; d <= rule.volBaseDays; d++) base.push(daySum(i - rule.volDay * d));
    const med = median(base);
    volumeRatio = med > 0 ? daySum(i) / med : null;
  }

  let priorHigh: number | null = null;
  let priorRange: number | null = null;
  if (i >= rule.rangeH) {
    let hi = -Infinity;
    let lo = Infinity;
    for (let k = i - rule.rangeH; k <= i - 1; k++) {
      hi = Math.max(hi, candles[k].high);
      lo = Math.min(lo, candles[k].low);
    }
    priorHigh = hi;
    priorRange = (hi - lo) / candles[i - 1].close;
  }

  const fired =
    volumeRatio !== null &&
    priorHigh !== null &&
    priorRange !== null &&
    volumeRatio >= rule.volMult &&
    priorRange <= rule.rangePct &&
    candles[i].close > priorHigh;
  return { volumeRatio, priorHigh, priorRange, fired };
}

// ---- forward step ----

/**
 * Advances one coin through every newly COMPLETED hourly bar. `candles` must
 * be sorted and reach BREAKOUT_WARMUP_BARS behind each new bar (the caller
 * fetches ~800). Bars still forming at `nowMs` are ignored. Returns the new
 * state, trades that closed, and 7-day outcomes that finished.
 */
export function stepBreakoutCoin(
  coin: string,
  prev: BreakoutCoinState,
  candles: readonly Candle[],
  nowMs: number,
  rule: BreakoutRuleParams = BREAKOUT_RULE,
  slippage: number = BREAKOUT_COSTS.slippagePerSide,
): {
  state: BreakoutCoinState;
  closed: BreakoutTrade[];
  finished: BreakoutOutcome[];
  newSignals: Array<PendingEntry & { coin: string }>;
} {
  const s: BreakoutCoinState = {
    ...prev,
    pending: prev.pending ? { ...prev.pending } : null,
    open: prev.open ? { ...prev.open } : null,
    watching: prev.watching.map((o) => ({ ...o })),
  };
  const closed: BreakoutTrade[] = [];
  const finished: BreakoutOutcome[] = [];
  const newSignals: Array<PendingEntry & { coin: string }> = [];

  for (let i = 0; i < candles.length; i++) {
    const c = candles[i];
    const tMs = c.time * 1000;
    if (tMs + HOUR_MS > nowMs) break; // still forming
    if (s.lastBarMs !== null && tMs <= s.lastBarMs) continue;

    // 7-day outcomes already running take this bar.
    const still: BreakoutOutcome[] = [];
    for (const o of s.watching) {
      if (o.refPx === null) o.refPx = c.open;
      o.maxHigh = Math.max(o.maxHigh, c.high);
      o.minLow = Math.min(o.minLow, c.low);
      o.bars += 1;
      if (o.bars >= rule.outcomeH) finished.push(o);
      else still.push(o);
    }
    s.watching = still;

    const r = breakoutReading(candles, i, rule);

    let isEntryBar = false;
    if (s.pending && !s.open) {
      s.open = {
        coin,
        ...s.pending,
        entryMs: tMs,
        entryPx: c.open * (1 + slippage),
      };
      s.pending = null;
      isEntryBar = true;
    }

    let tookSignal = false;
    if (s.open) {
      const target = s.open.entryPx * (1 + rule.targetPct);
      const stop = s.open.entryPx * (1 - rule.stopPct);
      let raw: number | null = null;
      let reason: BreakoutExitReason | null = null;
      if (!isEntryBar && c.open <= stop) [raw, reason] = [c.open, 'stop'];
      else if (!isEntryBar && c.open >= target) [raw, reason] = [c.open, 'target'];
      else if (c.low <= stop) [raw, reason] = [stop, 'stop'];
      else if (c.high >= target) [raw, reason] = [target, 'target'];
      else if ((tMs - s.open.entryMs) / HOUR_MS + 1 >= rule.maxHoldH) {
        [raw, reason] = [c.close, 'time'];
      }
      if (raw !== null && reason !== null) {
        closed.push({ ...s.open, exitMs: tMs, exitPx: raw * (1 - slippage), reason });
        s.open = null;
        s.cooldownUntilMs = tMs + HOUR_MS + rule.cooldownH * HOUR_MS;
      }
    } else if (
      !s.pending &&
      r.fired &&
      tMs >= rule.registeredAtMs &&
      tMs >= s.cooldownUntilMs
    ) {
      s.pending = {
        signalBarMs: tMs,
        signalClose: c.close,
        priorHigh: r.priorHigh!,
        volumeRatio: r.volumeRatio!,
      };
      tookSignal = true;
      newSignals.push({ coin, ...s.pending });
    }

    if (r.fired && !s.lastFired && tMs >= rule.registeredAtMs) {
      s.watching.push({
        coin,
        signalBarMs: tMs,
        refPx: null,
        // Finite placeholders so the state survives JSON; the first bar
        // after the signal replaces both (prices are always positive).
        maxHigh: 0,
        minLow: Number.MAX_VALUE,
        bars: 0,
        traded: tookSignal,
      });
    }

    s.lastBarMs = tMs;
    s.lastClose = c.close;
    s.lastFired = r.fired;
    s.volumeRatio = r.volumeRatio;
    s.priorHigh = r.priorHigh;
    s.priorRange = r.priorRange;
  }
  return { state: s, closed, finished, newSignals };
}

// ---- summary ----

export type BreakoutVerdict = 'collecting' | 'passing' | 'failing';

export interface BreakoutSummary {
  trades: number;
  weeks: number;
  open: number;
  winRate: number | null;
  meanNetLow: number | null;
  meanNetHigh: number | null;
  intervalHigh: [number, number] | null;
  pnlLow: number;
  pnlHigh: number;
  maxStopOvershoot: number;
  verdict: BreakoutVerdict;
  /** 7-day outcomes. */
  signalsFinished: number;
  signalsWatching: number;
  moonHits: number;
  craterHits: number;
  moonRate: number | null;
  craterRate: number | null;
}

export const isMoon = (o: BreakoutOutcome, rule: BreakoutRuleParams = BREAKOUT_RULE) =>
  o.refPx !== null && o.maxHigh >= (1 + rule.moonPct) * o.refPx;
export const isCrater = (o: BreakoutOutcome, rule: BreakoutRuleParams = BREAKOUT_RULE) =>
  o.refPx !== null && o.minLow <= (1 - rule.craterPct) * o.refPx;

export function summarizeBreakoutWatch(state: BreakoutWatchState): BreakoutSummary {
  const closed = state.trades.filter(
    (t): t is Required<BreakoutTrade> => t.exitPx !== undefined,
  );
  const coins = Object.values(state.coins);
  const open = coins.filter((c) => c.open).length;
  const low = closed.map((t) => netReturn(t.entryPx, t.exitPx, BREAKOUT_COSTS.feeLow));
  const high = closed.map((t) => netReturn(t.entryPx, t.exitPx, BREAKOUT_COSTS.feeHigh));
  const weeks = new Set(closed.map((t) => weekOf(t.entryMs))).size;
  const mean = (a: number[]) =>
    a.length ? a.reduce((x, y) => x + y, 0) / a.length : null;
  const meanHigh = mean(high);
  const interval = clusterMeanInterval(
    high,
    closed.map((t) => weekOf(t.entryMs)),
  );
  let maxStopOvershoot = 0;
  for (const t of closed) {
    if (t.reason !== 'stop') continue;
    const gross = t.exitPx / t.entryPx - 1;
    maxStopOvershoot = Math.max(maxStopOvershoot, -BREAKOUT_RULE.stopPct - gross);
  }

  const bar = BREAKOUT_PASS_BAR;
  let verdict: BreakoutVerdict = 'collecting';
  if (maxStopOvershoot > bar.maxStopOvershoot) verdict = 'failing';
  else if (closed.length >= bar.minTrades && weeks >= bar.minWeeks) {
    verdict =
      meanHigh !== null &&
      meanHigh > 0 &&
      interval !== null &&
      interval[0] > bar.minLowerBound
        ? 'passing'
        : 'failing';
  }

  const done = state.outcomes;
  const moonHits = done.filter((o) => isMoon(o)).length;
  const craterHits = done.filter((o) => isCrater(o)).length;
  return {
    trades: closed.length,
    weeks,
    open,
    winRate: closed.length ? high.filter((x) => x > 0).length / closed.length : null,
    meanNetLow: mean(low),
    meanNetHigh: meanHigh,
    intervalHigh: interval,
    pnlLow: low.reduce((a, b) => a + b, 0) * BREAKOUT_STAKE_DOLLARS,
    pnlHigh: high.reduce((a, b) => a + b, 0) * BREAKOUT_STAKE_DOLLARS,
    maxStopOvershoot,
    verdict,
    signalsFinished: done.length,
    signalsWatching: coins.reduce((a, c) => a + c.watching.length, 0),
    moonHits,
    craterHits,
    moonRate: done.length ? moonHits / done.length : null,
    craterRate: done.length ? craterHits / done.length : null,
  };
}

// ---- watchlist ----

export interface BreakoutWatchRow {
  coin: string;
  lastBarMs: number | null;
  lastClose: number | null;
  volumeRatio: number | null;
  priorHigh: number | null;
  priorRange: number | null;
  /** Last close relative to the prior 72-hour high (> 0 means above it). */
  vsPriorHigh: number | null;
  /** How many of the three conditions the last bar met. */
  conditionsMet: number;
  status: 'watching' | 'pending' | 'holding' | 'cooldown' | 'no data';
  cooldownUntilMs: number | null;
  open: BreakoutTrade | null;
  unrealized: number | null;
}

/** One row per watched coin, closest to a signal first. */
export function breakoutWatchlist(state: BreakoutWatchState): BreakoutWatchRow[] {
  const rule = BREAKOUT_RULE;
  const rows = rule.coins.map((coin): BreakoutWatchRow => {
    const c = state.coins[coin];
    if (!c || c.lastBarMs === null) {
      return {
        coin,
        lastBarMs: null,
        lastClose: null,
        volumeRatio: null,
        priorHigh: null,
        priorRange: null,
        vsPriorHigh: null,
        conditionsMet: 0,
        status: 'no data',
        cooldownUntilMs: null,
        open: null,
        unrealized: null,
      };
    }
    const vsPriorHigh =
      c.priorHigh !== null && c.lastClose !== null ? c.lastClose / c.priorHigh - 1 : null;
    const conditionsMet =
      Number(c.volumeRatio !== null && c.volumeRatio >= rule.volMult) +
      Number(c.priorRange !== null && c.priorRange <= rule.rangePct) +
      Number(vsPriorHigh !== null && vsPriorHigh > 0);
    const status: BreakoutWatchRow['status'] = c.open
      ? 'holding'
      : c.pending
        ? 'pending'
        : c.lastBarMs + HOUR_MS < c.cooldownUntilMs
          ? 'cooldown'
          : 'watching';
    return {
      coin,
      lastBarMs: c.lastBarMs,
      lastClose: c.lastClose,
      volumeRatio: c.volumeRatio,
      priorHigh: c.priorHigh,
      priorRange: c.priorRange,
      vsPriorHigh,
      conditionsMet,
      status,
      cooldownUntilMs: status === 'cooldown' ? c.cooldownUntilMs : null,
      open: c.open,
      unrealized:
        c.open && c.lastClose !== null
          ? (c.lastClose * (1 - BREAKOUT_COSTS.slippagePerSide)) / c.open.entryPx - 1
          : null,
    };
  });
  return rows.sort(
    (a, b) =>
      b.conditionsMet - a.conditionsMet || (b.volumeRatio ?? -1) - (a.volumeRatio ?? -1),
  );
}
